import { writeError } from "./_helpers/context.mjs";
import { lazyProperties } from "./_helpers/lazy-properties.mjs";
import { CheckKind } from "./checks/check-kind.mjs";
import { readVisitor } from "./readers/read-visitor.mjs";
export const name = "Float64";
export class Float64Type {
  constructor(options) {
    this.name = name;
    this._options = options !== undefined ? options : {};
    if (typeof options !== "function") {
      this._applyOptions();
    } else {
      lazyProperties(this, this._applyOptions, ["allowNaN", "allowInfinity", "allowNegativeZero"]);
    }
  }
  static fromJSON(options) {
    return new Float64Type(options);
  }
  toJSON() {
    return {
      name,
      allowNaN: this.allowNaN,
      allowInfinity: this.allowInfinity,
      allowNegativeZero: this.allowNegativeZero
    };
  }
  read(cx, reader, raw) {
    return reader.readFloat64(cx, raw, readVisitor({
      fromFloat64: input => {
        if (reader.trustInput) {
          return {
            ok: true,
            value: input
          };
        }
        return this.test(cx, input);
      }
    }));
  }
  write(writer, value) {
    return writer.writeFloat64(value);
  }
  test(cx, value) {
    if (typeof value !== "number") {
      return writeError(cx, {
        check: CheckKind.BaseType,
        expected: ["Float64"]
      });
    }
    if (!this.allowNaN && isNaN(value) || !this.allowInfinity && Math.abs(value) === Infinity || !this.allowNegativeZero && Object.is(value, -0)) {
      return writeError(cx, {
        check: CheckKind.Float64,
        allowNaN: this.allowNaN,
        allowInfinity: this.allowInfinity,
        allowNegativeZero: this.allowNegativeZero
      });
    }
    return {
      ok: true,
      value
    };
  }
  /**
   * Tests the equivalence of two valid float64 values.
   *
   * Two values are equivalent if they are both `NaN`, both `-0`, both `+0` or non-zero and
   * numerically equal.
   */
  equals(left, right) {
    return Object.is(left, right);
  }
  /**
   * Compares two valid float64 values.
   *
   * The values are ordered as follows:
   * - `-Infinity`
   * - Negative non-zero finite values
   * - `-0`
   * - `+0`
   * - Positive non-zero finite values
   * - `+Infinity`
   * - `NaN`
   *
   * @param left Left operand.
   * @param right Right operand.
   * @return Boolean indicating if `left <= right`
   */
  lte(left, right) {
    if (isNaN(right)) {
      return true;
    } else if (isNaN(left)) {
      return false;
    }
    if (left === 0 && right === 0) {
      return Object.is(left, -0) || Object.is(right, +0);
    }
    return left <= right;
  }
  clone(value) {
    return value;
  }
  diff(oldVal, newVal) {
    // We can't use an arithmetic difference due to possible precision loss
    return this.equals(oldVal, newVal) ? undefined : [oldVal, newVal];
  }
  patch(oldVal, diff) {
    return diff === undefined ? oldVal : diff[1];
  }
  reverseDiff(diff) {
    return diff === undefined ? undefined : [diff[1], diff[0]];
  }
  squash(diff1, diff2) {
    if (diff1 === undefined) {
      return diff2 === undefined ? undefined : [diff2[0], diff2[1]];
    } else if (diff2 === undefined) {
      return [diff1[0], diff1[1]];
    }
    return this.equals(diff1[0], diff2[1]) ? undefined : [diff1[0], diff2[1]];
  }
  _applyOptions() {
    if (this._options === undefined) {
      throw new Error("missing `_options` for lazy initialization");
    }
    const options = typeof this._options === "function" ? this._options() : this._options;
    const allowNaN = options.allowNaN !== undefined ? options.allowNaN : false;
    const allowInfinity = options.allowInfinity !== undefined ? options.allowInfinity : false;
    const allowNegativeZero = options.allowNegativeZero !== undefined ? options.allowNegativeZero : false;
    Object.assign(this, {
      allowNaN,
      allowInfinity,
      allowNegativeZero
    });
  }
}
export const $Float64 = new Float64Type();
